import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

import Icon from "@ats/src/components/shared/Icon";
import { distanceInWords } from "@shared/lib/time";
import { prettyErrorsArray } from "@shared/lib/formHelpers";
import WhatJobsSidebarActions from "./WhatJobsSidebarActions";

interface WhatJobsSidebarProps {
  isMissingAnyRequirements: boolean;
  errors: any;
  jobId: number;
  currentListing: any;
  listingData: any;
  hasPaymentMethod: boolean;
  isPurchasing: boolean;
  isPaidButProcessing: boolean;
  setIsPurchasing: (value: boolean) => void;
  setErrors: (errors: any) => void;
  setIsDirty: (value: boolean) => void;
  isExpired: boolean;
  isLive: boolean; // unexpired & active (not paused)
  isUnexpiredAndActiveOrPaused: boolean;
  expiresAt: any;
  publishedAt: any;
  listingUrl: any;
  isAdmin: boolean;
}

function WhatJobsSidebar(props: WhatJobsSidebarProps) {
  const {
    isMissingAnyRequirements,
    errors,
    jobId,
    currentListing,
    listingData,
    hasPaymentMethod,
    isPurchasing,
    isPaidButProcessing,
    setIsPurchasing,
    setErrors,
    setIsDirty,
    isExpired,
    isLive,
    isUnexpiredAndActiveOrPaused,
    expiresAt,
    publishedAt,
    listingUrl,
    isAdmin,
  } = props;

  const Purchased = () => {
    if (!isUnexpiredAndActiveOrPaused) {
      return null;
    }

    return (
      <>
        <Styled.Divider />
        <Styled.Purchased>
          {isLive ? (
            <>
              <Styled.PurchasedTitle>
                {`Purchased ${distanceInWords(publishedAt)}`}{" "}
                <span>({distanceInWords(expiresAt, false)} left)</span>
              </Styled.PurchasedTitle>
              {listingUrl ? (
                <Styled.PurchasedLink>
                  <Icon name="external-link" />
                  <a href={listingUrl} target="_blank" rel="noopener noreferrer">
                    View post on WhatJobs
                  </a>
                </Styled.PurchasedLink>
              ) : (
                <Styled.PurchasedLink>
                  <div>Could not find URL</div>
                </Styled.PurchasedLink>
              )}
            </>
          ) : currentListing?.status === "paused" ? (
            <>
              <Styled.PurchasedTitle>
                {`Purchased ${distanceInWords(publishedAt)}`}{" "}
                <span>({distanceInWords(expiresAt, false)} left)</span>
              </Styled.PurchasedTitle>
              <p>
                Your premium listing has been paused and is not currently visible on WhatJobs or its
                partner network. Unless it has expired, you can reactivate it at any time.
              </p>
            </>
          ) : (
            <p>Your listing is being processed.</p>
          )}
        </Styled.Purchased>
      </>
    );
  };

  const ProcessingPayment = (
    <>
      <Styled.Divider />
      <Styled.Purchased>
        <Styled.PurchasedTitle>Paid - Processing WhatJobs listing</Styled.PurchasedTitle>
        <p>
          Your listing is being published to WhatJobs. This page will update automatically when your
          post is live.
        </p>
        <p>
          If it takes more than a few minutes, contact us at{" "}
          <a href="mailto:support@polymer.co">support@polymer.co</a>.
        </p>
      </Styled.Purchased>
    </>
  );

  const shouldDisplayListingExpired =
    currentListing?.expired ||
    currentListing?.status === "inactive" ||
    (currentListing?.status === "draft" && currentListing?.hasExpiredListing);

  const WhatsIncluded = () => {
    const features = [
      "30-day premium distribution on WhatJobs",
      "Distribution across 500+ partner job boards",
      "Optimized candidate targeting",
      "Thousands of qualified job seekers",
      "Continuous visibility for 30 days",
      "Email job alerts to candidates",
    ];

    return (
      <Styled.WhatsIncluded>
        <Styled.WhatsIncludedTitle>What's included</Styled.WhatsIncludedTitle>
        {features.map((feature, index) => (
          <Styled.WhatsIncludedFeature key={index}>
            <Icon name="check" />
            {feature}
          </Styled.WhatsIncludedFeature>
        ))}
      </Styled.WhatsIncluded>
    );
  };

  return (
    <Styled.Sidebar>
      <Styled.SidebarSection>
        <Styled.Price>
          <h3>Purchase price</h3>
          <h3>$175</h3>
        </Styled.Price>
        {!isMissingAnyRequirements && !isUnexpiredAndActiveOrPaused && (
          <Styled.PurchaseResult>
            Clicking the purchase button below will list your job on WhatJobs with distribution
            across 500+ partner job boards and apply a <span>$175 charge</span> to the payment
            method associated with your Polymer subscription.
          </Styled.PurchaseResult>
        )}
        <p>
          Your job will be listed on WhatJobs and distributed across 500+ partner job boards for 30
          days from the time of purchase.
        </p>
        <WhatsIncluded />
        {isMissingAnyRequirements && !isUnexpiredAndActiveOrPaused && !isPaidButProcessing && (
          <p>Purchase is disabled until all WhatJobs requirements are met.</p>
        )}
        <WhatJobsSidebarActions
          isPurchasing={isPurchasing}
          isLive={isLive}
          isExpired={isExpired}
          isPaidButProcessing={isPaidButProcessing}
          isMissingAnyRequirements={isMissingAnyRequirements}
          jobId={jobId}
          currentListing={currentListing}
          listingData={listingData}
          hasPaymentMethod={hasPaymentMethod}
          setIsPurchasing={setIsPurchasing}
          setErrors={setErrors}
          setIsDirty={setIsDirty}
          isAdmin={isAdmin}
        />
      </Styled.SidebarSection>
      {currentListing && isUnexpiredAndActiveOrPaused && <Purchased />}
      {currentListing && isPaidButProcessing && ProcessingPayment}
      {shouldDisplayListingExpired && (
        <>
          <Styled.Divider />
          <Styled.Purchased>
            <p>
              Your previous WhatJobs listing has expired and is no longer visible on WhatJobs or its
              partner network.
            </p>
          </Styled.Purchased>
        </>
      )}
      {errors && Object.keys(errors).length > 0 && (
        <Styled.SidebarError>
          <Styled.SidebarErrorLabel>Form Errors:</Styled.SidebarErrorLabel>
          <Styled.SidebarErrorList>
            {prettyErrorsArray(errors).map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </Styled.SidebarErrorList>
        </Styled.SidebarError>
      )}
    </Styled.Sidebar>
  );
}

export default WhatJobsSidebar;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.Sidebar = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: WhatJobsSidebar_Sidebar;
    position: sticky;
    top: ${t.spacing[4]};
    h3 {
      ${[t.mt(1), t.pt("px"), t.text.base, t.text.bold]};
      color: ${t.dark ? t.color.gray[300] : t.color.black};
    }
    p {
      ${[t.text.xs, t.mt(3)]};
      color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
    }
    b {
      ${[t.text.bold]};
      color: ${t.dark ? t.color.gray[300] : t.color.black};
    }
  `;
});

Styled.SidebarSection = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: WhatJobsSidebar_SidebarSection;
    ${t.px(4)};

    button {
      ${t.mt(4)};
    }
  `;
});

Styled.Price = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: WhatJobsSidebar_Price;
    ${[t.text.bold, t.text.base, t.mb(4)]}
    display: flex;
    justify-content: space-between;
  `;
});

Styled.WhatsIncluded = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: WhatJobsSidebar_WhatsIncluded;
    ${[t.rounded.md, t.p(3), t.mb(6), t.mt(6)]}
    border: 1px solid ${t.dark ? t.color.gray[800] : t.color.gray[200]};
    background-color: ${t.dark ? t.color.gray[800] : t.color.white};
  `;
});

Styled.WhatsIncludedTitle = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: WhatJobsSidebar_WhatsIncludedTitle;
    ${[t.text.xs, t.text.bold, t.mb(2)]}
    color: ${t.dark ? t.color.gray[300] : t.color.black};
  `;
});

Styled.WhatsIncludedFeature = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: WhatJobsSidebar_WhatsIncludedFeature;
    ${[t.text.xs, t.py(1)]}
    display: flex;
    color: ${t.dark ? t.color.gray[400] : t.color.black};
    line-height: 1.4;
    svg {
      ${[t.mt("-px"), t.mr(1)]}
    }
  `;
});

Styled.PurchaseResult = styled.p((props) => {
  const t: any = props.theme;
  return css`
    label: WhatJobsSidebar_PurchaseResult;
    span {
      ${[t.text.bold]}
      color: ${t.dark ? t.color.gray[300] : t.color.gray[900]};
    }
  `;
});

Styled.Divider = styled.hr((props) => {
  const t: any = props.theme;
  return css`
    label: WhatJobsSidebar_Divider;
    ${[t.mb(0)]}
    margin-top: 2.25rem;
    width: 100%;
    border: none;
  `;
});

Styled.Purchased = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: WhatJobsSidebar_Purchased;
    ${t.px(4)}
    p {
      ${[t.mt(0), t.mb(4), t.text.xs]}
    }
  `;
});

Styled.PurchasedTitle = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: WhatJobsSidebar_PurchasedTitle;
    ${[t.text.xs, t.text.bold, t.mb(2)]}
    color: ${t.dark ? t.color.gray[300] : t.color.black};
    span {
      ${[t.text.normal]}
      color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
    }
  `;
});

Styled.PurchasedLink = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: WhatJobsSidebar_PurchasedLink;
    ${[t.text.xs, t.h(6)]}
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
    display: flex;
    align-items: center;
    overflow: hidden;
    svg {
      ${t.mr(2)}
      flex-shrink: 0;
    }
    div,
    a,
    button {
      color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
      text-align: start;
      padding: 0;
      appearance: none;
      outline: none;
      background: transparent;
      border: none;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    button:hover,
    button:focus,
    a:hover {
      text-decoration: underline;
    }
  `;
});

Styled.SidebarError = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: WhatJobsSidebar_SidebarError;
    ${[t.p(2), t.mt(6), t.mx(4), t.rounded.sm]}
    border: 1px solid ${t.dark ? t.color.red[400] : t.color.red[600]};
    background-color: ${t.dark ? t.color.gray[800] : t.color.red[100]};
    color: ${t.dark ? t.color.red[400] : t.color.red[600]};

    ${t.mq["md"]} {
      ${[t.px(2), t.py(1)]}
    }
  `;
});

Styled.SidebarErrorLabel = styled.span((props) => {
  const t: any = props.theme;
  return css`
    label: WhatJobsSidebar_SidebarErrorLabel;
    ${[t.text.sm, t.text.medium]}

    ${t.mq["md"]} {
      ${t.text.xs}
    }
  `;
});

Styled.SidebarErrorList = styled.ul((props) => {
  const t: any = props.theme;
  return css`
    label: WhatJobsSidebar_SidebarErrorList;
    ${[t.text.base, t.mt(4), t.pl(6)]}
    list-style-type: disc;
    margin: 0;
    padding-left: 1.5rem;

    li {
      ${t.mb(1)}
    }

    ${t.mq["md"]} {
      ${t.text.sm}
    }
  `;
});
